import useFormatter from "@/hooks/use-formatter.hook.ts";
import useGoalsHook from "@/hooks/use-goals.hook.tsx";
import { GoalKeyResultEditModal } from "@/pages/talent/goals/widgets/GoalKeyResultEditModal.tsx";
import { useKeyResultDelete } from "@/queries/goals.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiGoal, ApiGoalKeyResult, GoalMetricTypeItems } from "@/types/goals.types.ts";
import {
  Avatar,
  Button,
  ButtonIcon,
  ConfirmationModal,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Text,
  Title,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface GoalKeyResultItemProps {
  keyResult: ApiGoalKeyResult;
  goal: ApiGoal;
}

export function GoalKeyResultItem({ keyResult, goal }: GoalKeyResultItemProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { showInfo } = useToasts();
  const { renderTimeAgoShort } = useFormatter();
  const { renderKeyResultProgress, renderKeyResultValue, getKeyResultIcon } = useGoalsHook();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const keyResultDelete = useKeyResultDelete();

  const handleEdit = () => {
    openModal(GoalKeyResultEditModal, { goalId: goal.id, keyResultId: keyResult.id });
  };

  const handleDelete = () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("goals:delete_metric_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async () => {
        setIsDeleting(true);
        const result = await keyResultDelete.mutateAsync({
          goalId: goal.id,
          keyResultId: keyResult.id,
        });

        analyticsService.trackEvent(analyticEvents.goals.metricDeleted, {
          [analyticProperties.id]: keyResult.id,
        });

        if (result?.success) {
          showInfo({
            title: t("goals:metric_deleted"),
          });
        }
        setIsDeleting(false);
      },
    });
  };

  const getProgressInformation = () => {
    let progress = keyResult.progress ?? 0;
    progress = Math.round(progress * 10000) / 100;
    return progress;
  };

  const isCompleted = (keyResult.progress ?? 0) >= 1;
  const metricTitle =
    GoalMetricTypeItems().find((item) => item.id === keyResult.metric_type)?.title ?? "";

  const updatedAt = keyResult.datetime_updated_at
    ? renderTimeAgoShort(keyResult.datetime_updated_at).toLowerCase()
    : null;

  return (
    <Stack
      className={twMerge(
        "bg-dark/5 border border-dark/5 rounded-xs px-4 py-2",
        isDeleting && "opacity-50"
      )}
      gap={"md"}
    >
      <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
        <ListItem
          className={"border-0"}
          icon={
            <Avatar
              className={isCompleted ? "bg-positive text-light" : ""}
              icon={
                isCompleted ? (
                  <FontAwesomeIcon icon="fa-light fa-check" />
                ) : (
                  getKeyResultIcon(keyResult.metric_type)
                )
              }
            />
          }
          titleSlot={
            <Text
              className={twMerge("font-semibold", isCompleted ? "text-secondary line-through" : "")}
            >
              {keyResult.title}
            </Text>
          }
          captionSlot={
            <DotSeparator>
              {renderKeyResultProgress(keyResult)}
              <Text className={"text-secondary"}>{updatedAt}</Text>
            </DotSeparator>
          }
          value={`${getProgressInformation()}%`}
        />
        <ButtonIcon
          icon={
            <FontAwesomeIcon icon={`fa-light ${!expanded ? "fa-angle-right" : "fa-angle-down"}`} />
          }
          variant={"plain"}
          onClick={() => {
            setExpanded(!expanded);
          }}
        />
      </Stack>
      {expanded && (
        <Stack gap={"md"}>
          <Stack>
            {keyResult.description && (
              <ListItem
                preTitle={t("common:description")}
                captionSlot={<span className="whitespace-pre-wrap">{keyResult.description}</span>}
              />
            )}

            <ListItem
              preTitle={t("goals:start_value")}
              captionSlot={renderKeyResultValue(keyResult.start_value, keyResult.metric_type)}
            />

            <ListItem
              preTitle={t("goals:current_value")}
              captionSlot={renderKeyResultValue(keyResult.current_value, keyResult.metric_type)}
              value={t("goals:last_updated", { time: updatedAt })}
            />

            <ListItem
              preTitle={t("goals:target_value")}
              captionSlot={renderKeyResultValue(keyResult.target_value, keyResult.metric_type)}
            />

            <ListItem
              preTitle={t("goals:metric_type")}
              captionSlot={metricTitle}
              className={"border-0"}
            />
          </Stack>

          {goal.acl?.can_edit && (
            <Stack direction={"horizontal"} gap={"sm"} className={"pb-2"}>
              <Button variant={"secondary"} onClick={handleEdit}>
                {t("common:edit")}
              </Button>
              <DropdownMenu
                trigger={
                  <ButtonIcon
                    icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                    variant={"secondary"}
                  ></ButtonIcon>
                }
              >
                <DropdownMenuItem
                  variant={"menuDanger"}
                  onClick={handleDelete}
                  title={t("common:delete") + "..."}
                />
              </DropdownMenu>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}

interface GoalKeyResultsProps {
  keyResults: ApiGoalKeyResult[];
  onAddMetric: () => void;
  goal: ApiGoal;
  compact?: boolean;
}

export function GoalKeyResults({
  keyResults,
  goal,
  onAddMetric,
  compact = false,
}: GoalKeyResultsProps) {
  const { t } = useTranslation();

  return (
    <>
      <Title
        size={"xs"}
        header={t("goals:metrics")}
        caption={keyResults.length == 0 ? t("goals:no_metrics") : ""}
        paddingBottom
        paddingTop
      />
      <Stack gap={"md"}>
        {keyResults.length > 0 ? (
          <Stack gap={"sm"}>
            {keyResults.map((keyResult) => (
              <GoalKeyResultItem key={`metric-${keyResult.id}`} keyResult={keyResult} goal={goal} />
            ))}
          </Stack>
        ) : (
          <></>
        )}

        {!compact && goal.acl?.can_edit && (
          <Stack direction={"horizontal"} gap={"sm"}>
            <Button variant={"secondary"} onClick={onAddMetric}>
              {t("common:add")}
            </Button>
          </Stack>
        )}
      </Stack>
    </>
  );
}
